import React, { useEffect } from "react";
import { node, oneOf, string } from "prop-types";
import { Link, useLocation } from "react-router";
import { Box, Breadcrumbs, Container, Stack, Typography } from "@mui/material";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import { usePageView } from "../../hooks/usePageView";
import googleAnalytics from "shared/analytics";

export const appPageTitleStyle = (breakpoints) => ({
  fontSize: 28,
  fontWeight: 700,
  [breakpoints.up("md")]: {
    fontSize: 36,
  },
});

const AppPage = ({ title = "", docTitle = "", bannerHeight = "", banner = null, children }) => {
  // Update document title and trigger page view event
  usePageView(docTitle || title);

  const location = useLocation();
  const isXl = bannerHeight === "Xl";

  // Reset scroll position on mount
  useEffect(() => {
    const scrollContainer = document.getElementById("scroll-container");
    if (scrollContainer) {
      scrollContainer.scrollTo(0, 0);
    }
  }, [location.pathname]); // Reset when pathname changes

  const bannerHeights = {
    md: 100,
    lg: 150,
    xl: 240,
  };

  const baseBannerStyles = (theme) => ({
    backgroundColor: theme.palette.secondary.main,
    display: "flex",
    color: theme.palette.text.light,
    flexDirection: "column",
    minHeight: bannerHeights["md"],
    justifyContent: "center",
    alignItems: "center",
    [theme.breakpoints.up("md")]: {
      minHeight: bannerHeights["lg"],
      "& h2": {
        fontSize: 45,
      },
    },
  });

  const xlBannerStyles = (theme) => ({
    ...baseBannerStyles(theme),
    minHeight: bannerHeights["lg"],
    justifyContent: "flex-start",
    paddingTop: theme.spacing(4.5),
    [theme.breakpoints.up("md")]: {
      minHeight: bannerHeights["xl"],
    },
    "@media (max-height: 900px)": {
      minHeight: 150,
      paddingTop: theme.spacing(2.5),
    },
  });

  return (
    <Box
      component="main"
      sx={{ position: "relative", minWidth: "100%", overflow: "auto" }}
      id="scroll-container"
    >
      <Box
        component="section"
        sx={(theme) => ({
          flex: "1 1 100%",
          minWidth: "100%",
          marginBottom: "75px",
          paddingBottom: theme.spacing(4),
        })}
        id="main-content"
      >
        {banner}
        {title && (
          <Box
            component="section"
            sx={(theme) => (isXl ? xlBannerStyles(theme) : baseBannerStyles(theme))}
          >
            <Container
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "flex-start",
                height: "100%",
                width: "100%",
              }}
            >
              {location.pathname.includes("/tools") ? (
                <Stack
                  direction="row"
                  sx={{
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    px: 4,
                  }}
                >
                  <Typography
                    variant="h2"
                    component="h1"
                    sx={(theme) => ({
                      ...appPageTitleStyle(theme.breakpoints),
                      textAlign: "center",
                    })}
                  >
                    {title}
                  </Typography>
                </Stack>
              ) : (
                <Stack direction="column" spacing={1} sx={{ width: "100%", px: 0 }}>
                  <Breadcrumbs aria-label="breadcrumb" sx={{ color: "inherit" }}>
                    <Box
                      component={Link}
                      to="/"
                      onClick={() =>
                        googleAnalytics.trackBreadcrumbClick({
                          whereTo: "Home",
                          currentPage: location.pathname,
                        })
                      }
                      sx={{
                        color: "inherit",
                        display: "flex",
                        alignItems: "center",
                        textDecoration: "none",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      <HomeRoundedIcon fontSize="small" style={{ marginRight: "0.3em" }} />
                      Home
                    </Box>
                    <Typography sx={{ color: "inherit", display: "flex", alignItems: "center" }}>
                      {title}
                    </Typography>
                  </Breadcrumbs>
                  <Typography
                    variant="h2"
                    component="h1"
                    sx={(theme) => appPageTitleStyle(theme.breakpoints)}
                  >
                    {title}
                  </Typography>
                </Stack>
              )}
            </Container>
          </Box>
        )}
        {children}
      </Box>
    </Box>
  );
};

AppPage.propTypes = {
  title: string,
  docTitle: string,
  bannerHeight: oneOf(["", "Xl"]),
  banner: node,
  children: node,
};

export default AppPage;
